import React, { useCallback, useMemo } from 'react';
import { Button } from 'antd';
import { getUserDetails } from '../../../../utils/helper';
import type { ReportStatus } from '../../types';

interface StatusActionsProps {
  statusList: ReportStatus[];
  statusLoading: boolean;
  currentReportStatus: string | undefined;
  isSignedOff: boolean;
  onAction: (statusCode: string) => void;
}

const SIGNOFF_ICON = (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0.199951 2.2001C0.199951 1.3176 0.917451 0.600098 1.79995 0.600098H8.19995C9.08245 0.600098 9.79995 1.3176 9.79995 2.2001V12.6276C9.79995 13.2676 9.08745 13.6476 8.55495 13.2926L4.99995 10.9201L1.44495 13.2926C0.912451 13.6476 0.199951 13.2651 0.199951 12.6276V2.2001ZM1.79995 1.8001C1.57995 1.8001 1.39995 1.9801 1.39995 2.2001V11.8801L4.33495 9.9251C4.73745 9.6576 5.26245 9.6576 5.66495 9.9251L8.59995 11.8801V2.2001C8.59995 1.9801 8.41995 1.8001 8.19995 1.8001H1.79995Z" fill="white"/>
  </svg>
);

const getButtonProps = (statusCode: string) => {
  switch (statusCode) {
    case 'SIGNEDOFF':
      return { type: 'primary' as const, className: 'footer-btn-signoff' };
    case 'DRAFTED':
      return { type: 'default' as const, className: 'footer-btn' };
    default:
      return { type: 'default' as const, className: 'footer-btn' };
  }
};

const StatusActions: React.FC<StatusActionsProps> = ({
  statusList,
  statusLoading,
  currentReportStatus,
  isSignedOff,
  onAction,
}) => {
  const userType = getUserDetails()?.user_type;

  const getDisabledState = useCallback((statusCode: string) => {
    const orderMap: Record<string, number> = {};
    statusList.forEach(s => { orderMap[s.status_code] = s.status_order; });

    const currentOrder = orderMap[currentReportStatus || ''] || 0;
    const targetOrder = orderMap[statusCode] || 0;

    switch (statusCode) {
      case 'DRAFTED':
        return isSignedOff || currentOrder > targetOrder;
      case 'SIGNEDOFF':
        return !['hod', 'radiologist'].includes(userType);
      default:
        return false;
    }
  }, [statusList, currentReportStatus, isSignedOff, userType]);

  const buttons = useMemo(() => {
    const fallback: ReportStatus[] = [
      { status_id: 1, status_code: 'DRAFTED', status_label: 'DRAFT', status_order: 1 },
      { status_id: 3, status_code: 'SIGNEDOFF', status_label: 'SIGN OFF', status_order: 3 },
    ];

    const list = statusLoading || !statusList.length ? fallback : statusList;

    return list
      .filter(s => !(isSignedOff && s.status_code === 'DRAFTED'))
      .map(status => {
        const props = getButtonProps(status.status_code);
        const isUpdatingSignoff = isSignedOff && status.status_code === 'SIGNEDOFF';
        const className = isUpdatingSignoff ? 'footer-btn-signoff footer-btn-signoff-update' : props.className;
        const label = isUpdatingSignoff ? 'Update Sign-off' : status.status_label;
        return (
          <Button
            key={status.status_id}
            className={className}
            type={props.type}
            disabled={getDisabledState(status.status_code)}
            onClick={() => onAction(status.status_code)}
          >
            {status.status_code === 'SIGNEDOFF' && SIGNOFF_ICON}
            {label}
          </Button>
        );
      });
  }, [statusList, statusLoading, isSignedOff, currentReportStatus, getDisabledState, onAction]);

  return <>{buttons}</>;
};

export default StatusActions;
